import { useState, useEffect, useCallback } from 'react';
import type { NotificationPreference, PreferenceType, PreferencesMap } from '@/types/notifications';
import { PREFERENCE_TYPE_LABELS } from '@/types/notifications';
import {
  getMyPreferences,
  upsertPreference,
  setGlobalPreference,
  setRadioNotificationPreference,
  setProgramNotificationPreference,
  setAnnouncerNotificationPreference,
  setPodcastNotificationPreference,
  setContestNotificationPreference,
  setRequestNotificationPreference,
  setAdminNoticePreference,
  resetAllPreferences,
} from '@/services/notificationPreferences';
import { mockNotificationPreferences } from '@/mocks/notificationCenter';
import { SwalAlert } from '@/hooks/useSweetAlert';

// ─── Constants ──────────────────────────────────────────────

type TabKey = 'global' | 'radios' | 'programs' | 'announcers' | 'podcasts' | 'contests' | 'requests' | 'admin';

const tabsList: { key: TabKey; label: string; icon: string; type: PreferenceType }[] = [
  { key: 'global', label: 'Global', icon: 'ri-global-line', type: 'global' },
  { key: 'radios', label: 'Radios', icon: 'ri-broadcast-line', type: 'radio' },
  { key: 'programs', label: 'Programas', icon: 'ri-calendar-check-line', type: 'program' },
  { key: 'announcers', label: 'Locutores', icon: 'ri-mic-line', type: 'announcer' },
  { key: 'podcasts', label: 'Podcasts', icon: 'ri-headphone-line', type: 'podcast' },
  { key: 'contests', label: 'Concursos', icon: 'ri-trophy-line', type: 'contest' },
  { key: 'requests', label: 'Solicitudes', icon: 'ri-message-2-line', type: 'request' },
  { key: 'admin', label: 'Avisos', icon: 'ri-megaphone-line', type: 'admin_notice' },
];

// ─── Helpers ─────────────────────────────────────────────────

function groupPreferences(prefs: NotificationPreference[]): PreferencesMap {
  const map: PreferencesMap = {} as PreferencesMap;
  for (const p of prefs) {
    if (!map[p.preference_type]) {
      map[p.preference_type] = [];
    }
    map[p.preference_type].push(p);
  }
  return map;
}

// ─── Component ──────────────────────────────────────────────

export default function PreferencesPanel() {
  const [prefs, setPrefs] = useState<NotificationPreference[]>([]);
  const [grouped, setGrouped] = useState<PreferencesMap>({} as PreferencesMap);
  const [activeTab, setActiveTab] = useState<TabKey>('global');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingMock, setUsingMock] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // ── Load ────────────────────────────────────────────────

  const loadPrefs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMyPreferences();
      if (data.length > 0) {
        setPrefs(data);
        setGrouped(groupPreferences(data));
        setUsingMock(false);
      } else {
        // Fallback a mock
        setPrefs(mockNotificationPreferences);
        setGrouped(groupPreferences(mockNotificationPreferences));
        setUsingMock(true);
      }
    } catch {
      setPrefs(mockNotificationPreferences);
      setGrouped(groupPreferences(mockNotificationPreferences));
      setUsingMock(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrefs();
  }, [loadPrefs]);

  // ── Toggle ──────────────────────────────────────────────

  const togglePref = useCallback(async (pref: NotificationPreference) => {
    const newEnabled = !pref.enabled;
    // Optimistic update
    setPrefs((prev) =>
      prev.map((p) => (p.id === pref.id ? { ...p, enabled: newEnabled } : p)),
    );
    setGrouped((prev) => {
      const next = { ...prev };
      const key = pref.preference_type;
      if (next[key]) {
        next[key] = next[key].map((p) => (p.id === pref.id ? { ...p, enabled: newEnabled } : p));
      }
      return next;
    });
    setSaved(false);

    if (!usingMock) {
      try {
        if (pref.reference_id) {
          await setByTypeAndRef(pref.preference_type, pref.reference_id, newEnabled);
        } else {
          await setByType(pref.preference_type, newEnabled);
        }
      } catch {
        // revert optimistic update
        setPrefs((prev) =>
          prev.map((p) => (p.id === pref.id ? { ...p, enabled: !newEnabled } : p)),
        );
      }
    }
  }, [usingMock]);

  const toggleAllCurrent = async (enable: boolean) => {
    const currentType = tabsList.find((t) => t.key === activeTab)?.type;
    if (!currentType) return;

    const targetIds = prefs
      .filter((p) => p.preference_type === currentType)
      .map((p) => p.id);

    setPrefs((prev) =>
      prev.map((p) => (targetIds.includes(p.id) ? { ...p, enabled: enable } : p)),
    );
    setGrouped((prev) => {
      const next = { ...prev };
      if (next[currentType]) {
        next[currentType] = next[currentType].map((p) => ({ ...p, enabled: enable }));
      }
      return next;
    });
    setSaved(false);

    if (!usingMock) {
      for (const p of prefs.filter((p) => targetIds.includes(p.id))) {
        try {
          if (p.reference_id) {
            await setByTypeAndRef(p.preference_type, p.reference_id, enable);
          } else {
            await setByType(p.preference_type, enable);
          }
        } catch { /* skip individual failures */ }
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    // In mock mode or when already synced, just show saved
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    setSaving(false);
  };

  const handleReset = async () => {
    const result = await SwalAlert.confirm(
      'Restablecer preferencias',
      'Se eliminarán todas tus preferencias personalizadas. Las notificaciones volverán a sus valores por defecto (todo habilitado).',
    );
    if (result.isConfirmed) {
      try {
        if (!usingMock) await resetAllPreferences();
      } catch { /* mock */ }
      const reset = mockNotificationPreferences.map((p) => ({ ...p, enabled: true }));
      setPrefs(reset);
      setGrouped(groupPreferences(reset));
      SwalAlert.toast('Preferencias restablecidas', 'success');
    }
  };

  // ── Helpers ─────────────────────────────────────────────

  async function setByType(type: PreferenceType, enabled: boolean) {
    switch (type) {
      case 'global': await setGlobalPreference(enabled); break;
      case 'contest': await setContestNotificationPreference(enabled); break;
      case 'request': await setRequestNotificationPreference(enabled); break;
      case 'admin_notice': await setAdminNoticePreference(enabled); break;
      default: await upsertPreference({ preference_type: type, reference_id: null, enabled }); break;
    }
  }

  async function setByTypeAndRef(type: PreferenceType, refId: string, enabled: boolean) {
    switch (type) {
      case 'radio': await setRadioNotificationPreference(refId, enabled); break;
      case 'program': await setProgramNotificationPreference(refId, enabled); break;
      case 'announcer': await setAnnouncerNotificationPreference(refId, enabled); break;
      case 'podcast': await setPodcastNotificationPreference(refId, enabled); break;
      default: await upsertPreference({ preference_type: type, reference_id: refId, enabled }); break;
    }
  }

  // ── Current tab data ───────────────────────────────────

  const currentType = tabsList.find((t) => t.key === activeTab)?.type || 'global';
  const currentPrefs = grouped[currentType] || [];
  const enabledCount = currentPrefs.filter((p) => p.enabled).length;
  const totalCount = currentPrefs.length;
  const hasGlobal = grouped.global && grouped.global.length > 0;
  const globalEnabled = hasGlobal ? grouped.global[0].enabled : true;

  // ── Render ─────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-bold text-foreground-950">Preferencias de Notificación</h2>
          <p className="text-xs text-foreground-500 mt-0.5">Elegí qué notificaciones querés recibir y de quiénes</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="px-3 py-1.5 text-xs font-medium text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
          >
            Restablecer
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
              saved
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-primary-500 text-background-50 hover:bg-primary-600'
            }`}
          >
            {saving ? (
              <span className="flex items-center gap-1.5">
                <i className="ri-loader-4-line animate-spin text-sm"></i>Guardando
              </span>
            ) : saved ? (
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line text-sm"></i>Guardado
              </span>
            ) : (
              'Guardar'
            )}
          </button>
        </div>
      </div>

      {/* Global toggle highlight */}
      {activeTab !== 'global' && hasGlobal && (
        <div className={`rounded-xl p-3 mb-4 flex items-center gap-3 ${
          globalEnabled ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
            globalEnabled ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'
          }`}>
            <i className={`${globalEnabled ? 'ri-notification-3-line' : 'ri-notification-off-line'} text-sm`}></i>
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold text-foreground-900">
              {globalEnabled ? 'Notificaciones globales activadas' : 'Notificaciones globales desactivadas'}
            </p>
            <p className="text-[10px] text-foreground-500 mt-0.5">
              {globalEnabled
                ? 'Las notificaciones se entregan según tus preferencias por categoría.'
                : 'Todas las notificaciones están silenciadas. Activalas en la pestaña Global.'}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-5 bg-background-100 rounded-2xl p-1 overflow-x-auto no-scrollbar">
        {tabsList.map((tab) => {
          const prefsForTab = grouped[tab.type] || [];
          const hasDisabled = prefsForTab.some((p) => !p.enabled);
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all cursor-pointer shrink-0 ${
                activeTab === tab.key
                  ? 'bg-primary-500 text-background-50'
                  : `text-foreground-600 hover:text-foreground-800 hover:bg-background-50 ${
                      hasDisabled ? 'text-accent-600' : ''
                    }`
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
              {hasDisabled && activeTab !== tab.key && (
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
              )}
            </button>
          );
        })}
      </div>

      {/* LOADING */}
      {loading && (
        <div className="space-y-1.5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-background-50 rounded-xl p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-background-200 shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 rounded-md bg-background-200"></div>
                  <div className="h-3 w-48 rounded-md bg-background-200"></div>
                </div>
                <div className="w-10 h-6 rounded-full bg-background-200 shrink-0"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ERROR */}
      {!loading && error && (
        <div className="text-center py-16 bg-background-50 rounded-2xl">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-red-500 text-xl"></i>
          </div>
          <h3 className="text-foreground-800 font-bold text-sm mb-1">Error al cargar</h3>
          <p className="text-foreground-400 text-xs max-w-xs mx-auto">{error}</p>
          <button
            onClick={loadPrefs}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-accent-500 text-background-50 text-xs font-bold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-sm"></i>
            Reintentar
          </button>
        </div>
      )}

      {/* EMPTY */}
      {!loading && !error && currentPrefs.length === 0 && (
        <div className="bg-background-50 rounded-2xl p-10 text-center">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-foreground-100 flex items-center justify-center">
            <i className="ri-notification-off-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600 text-sm font-medium">Sin preferencias configuradas</p>
          <p className="text-foreground-400 text-xs mt-1 max-w-xs mx-auto">
            {activeTab === 'radios'
              ? 'Empezá a seguir radios para configurar sus notificaciones.'
              : activeTab === 'programs'
                ? 'Agregá programas a tus favoritos para gestionar sus avisos.'
                : 'No hay elementos de este tipo para configurar.'}
          </p>
        </div>
      )}

      {/* LIST */}
      {!loading && !error && currentPrefs.length > 0 && (
        <>
          {/* Actions bar */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-foreground-500">
              {enabledCount} de {totalCount} {totalCount === 1 ? 'activa' : 'activas'}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => toggleAllCurrent(true)}
                className="px-2.5 py-1 text-[10px] font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-200 rounded-md cursor-pointer whitespace-nowrap transition-colors"
              >
                Activar todas
              </button>
              <button
                onClick={() => toggleAllCurrent(false)}
                className="px-2.5 py-1 text-[10px] font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-200 rounded-md cursor-pointer whitespace-nowrap transition-colors"
              >
                Desactivar todas
              </button>
            </div>
          </div>

          <div className="space-y-1">
            {currentPrefs.map((pref) => (
              <div
                key={pref.id}
                className={`bg-background-50 rounded-xl px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-background-100/80 transition-all ${
                  !pref.enabled ? 'opacity-50' : ''
                }`}
                onClick={() => togglePref(pref)}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                  pref.enabled
                    ? 'bg-primary-100 text-primary-600'
                    : 'bg-foreground-100 text-foreground-400'
                }`}>
                  <i className={`${tabsList.find((t) => t.type === pref.preference_type)?.icon || 'ri-notification-line'} text-base`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-foreground-900">
                    {pref.reference_id
                      ? pref.reference_id.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                      : PREFERENCE_TYPE_LABELS[pref.preference_type]}
                  </h4>
                  <p className="text-[11px] text-foreground-500 mt-0.5">
                    {pref.preference_type === 'global' && 'Controla todas las notificaciones del sistema'}
                    {pref.preference_type === 'radio' && `Radio seguida · ${pref.reference_id}`}
                    {pref.preference_type === 'program' && `Programa · ${pref.reference_id}`}
                    {pref.preference_type === 'announcer' && `Locutor · ${pref.reference_id}`}
                    {pref.preference_type === 'podcast' && `Podcast · ${pref.reference_id}`}
                    {pref.preference_type === 'contest' && 'Concursos, trivias y sorteos'}
                    {pref.preference_type === 'request' && 'Solicitudes de canciones y saludos'}
                    {pref.preference_type === 'admin_notice' && 'Avisos administrativos y del sistema'}
                  </p>
                </div>
                <div
                  className={`w-10 h-6 rounded-full relative transition-colors shrink-0 ${
                    pref.enabled ? 'bg-primary-500' : 'bg-foreground-300'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${
                      pref.enabled ? 'left-[18px]' : 'left-0.5'
                    }`}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}